import React, { useEffect, useRef, useState } from "react";

/**
 * Click-to-edit text field. Renders as plain text until clicked, then becomes
 * an input / textarea. Saves on blur or Enter (single-line). Escape cancels.
 */
export const InlineEditText = ({
    value,
    onSave,
    placeholder = "",
    className = "",
    multiline = false,
    dataTestId,
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    const ref = useRef(null);

    useEffect(() => {
        setDraft(value || "");
    }, [value]);

    useEffect(() => {
        if (editing && ref.current) {
            ref.current.focus();
            ref.current.select?.();
        }
    }, [editing]);

    const commit = () => {
        setEditing(false);
        const trimmed = (draft || "").trim();
        if (trimmed !== (value || "").trim()) onSave(trimmed);
    };

    const cancel = () => {
        setDraft(value || "");
        setEditing(false);
    };

    if (!editing) {
        const display = value && value.length > 0 ? value : placeholder;
        const isPlaceholder = !value || value.length === 0;
        return (
            <div
                onClick={() => setEditing(true)}
                data-testid={dataTestId}
                className={`${className} cursor-text hover:bg-white/5 rounded-sm px-1 -mx-1 transition-colors ${
                    isPlaceholder ? "italic text-slate-500" : ""
                }`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setEditing(true);
                    }
                }}
            >
                {multiline
                    ? display.split("\n").map((line, i) => (
                          <React.Fragment key={i}>
                              {line || "\u00A0"}
                              {i < display.split("\n").length - 1 && <br />}
                          </React.Fragment>
                      ))
                    : display}
            </div>
        );
    }

    const commonProps = {
        ref,
        value: draft,
        onChange: (e) => setDraft(e.target.value),
        onBlur: commit,
        onKeyDown: (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                cancel();
            } else if (e.key === "Enter" && !multiline) {
                e.preventDefault();
                commit();
            }
        },
        placeholder,
        "data-testid": dataTestId ? `${dataTestId}-editing` : undefined,
        className: `${className} bg-[#0A1128] border border-[#00A3FF]/50 rounded-sm px-1 -mx-1 outline-none w-full`,
    };

    return multiline ? (
        <textarea rows={2} {...commonProps} />
    ) : (
        <input type="text" {...commonProps} />
    );
};

export default InlineEditText;
